import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Pencil, GitMerge, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { KINDS, errMsg, fmtMoney, fmtPct, useApi } from "@/lib/api";

const FILTERS = [{ value: "all", label: "All" }, ...KINDS];

function ClientAccess({ ent, api }) {
  const [st, setSt] = useState(null);
  const load = useCallback(() => {
    api.get(`/entities/${ent.id}/client-access`).then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [api, ent.id]);
  useEffect(() => { load(); }, [load]);
  if (!st) return null;
  const label = { client: "Client login active", invited: "Invited — hasn't created a login yet", none: "No email saved", new: "No client login yet" }[st.status] || "Email can't be used for a client login";
  const revoke = async () => {
    if (!window.confirm("Remove this person's client login and every book shared with them?")) return;
    try {
      await api.delete(`/entities/${ent.id}/client-access`);
      toast.success("Client access removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 text-xs text-slate-600 mt-2" data-testid="entity-client-access">
      <span data-testid="entity-client-status">{label}{st.books_shared ? ` · ${st.books_shared} book${st.books_shared > 1 ? "s" : ""} shared` : ""}</span>
      {(st.status === "client" || st.status === "invited" || st.books_shared > 0) && (
        <Button variant="ghost" size="sm" className="text-red-700 h-7" onClick={revoke} data-testid="entity-client-revoke">Remove client access</Button>
      )}
    </div>
  );
}

function EditDialog({ ent, all, onClose, onSaved }) {
  const api = useApi();
  const { me } = useApp();
  const admin = me?.role === "admin" || me?.role === "owner";
  const person = ent?.kind === "individual";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [target, setTarget] = useState("");
  useEffect(() => {
    setName(ent?.name || "");
    setEmail(ent?.email || "");
    setTarget("");
  }, [ent]);
  const save = async () => {
    try {
      const body = admin && person && ent.id ? { name, email } : { name };
      if (ent.id) await api.patch(`/entities/${ent.id}`, body);
      else await api.post("/entities", { name, kind: ent.kind });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const merge = async () => {
    try {
      await api.post(`/entities/${ent.id}/merge`, { target_id: target });
      toast.success("Merged — balances and ownership moved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${ent.name}"? This removes its balances in every month end, every ownership link to or from it, and any client access. It can't be undone.`)) return;
    try {
      const r = await api.delete(`/entities/${ent.id}`);
      const n = (c, w) => `${c} ${w}${c === 1 ? "" : "s"}`;
      toast.success(`Deleted — ${n(r.data.balances_removed, "balance line")} and ${n(r.data.ownership_links_removed, "ownership link")} removed`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!ent} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="entity-edit-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{ent?.id ? "Edit entity" : "New entity"}</DialogTitle></DialogHeader>
        <label className="small-label">Name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="entity-name-input" />
        {ent?.id && person && admin && (
          <div className="mt-4">
            <label className="small-label">Client email</label>
            <p className="text-xs text-slate-500 mt-1">Used to email this person their family statement book. They get a private login that can only open books you share.</p>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="mt-2" data-testid="entity-email-input" />
            <ClientAccess ent={ent} api={api} />
          </div>
        )}
        {ent?.id && (
          <div className="mt-4 border-t pt-4">
            <label className="small-label">Merge duplicate into</label>
            <p className="text-xs text-slate-500 mt-1">Use when the same entity appears twice with different codes. Its balances, codes and ownership move to the chosen entity.</p>
            <div className="flex gap-2 mt-2">
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="rounded-sm" data-testid="entity-merge-select"><SelectValue placeholder="Choose entity" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {all.filter((x) => x.id !== ent.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" disabled={!target} onClick={merge} data-testid="entity-merge-button"><GitMerge className="h-4 w-4 mr-1" />Merge</Button>
            </div>
          </div>
        )}
        {ent?.id && admin && (
          <div className="mt-4 border-t pt-4" data-testid="entity-delete-section">
            <label className="small-label">Delete entity</label>
            <p className="text-xs text-slate-500 mt-1">Removes this entity, its balances in every month end and its ownership links. To combine duplicates, use Merge instead.</p>
            <Button variant="outline" className="mt-2 text-red-700 border-red-200 hover:bg-red-50" onClick={remove} data-testid="entity-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete entity</Button>
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={!name.trim()} data-testid="entity-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KindSelect({ ent, onChange, disabled }) {
  return (
    <Select value={ent.kind} onValueChange={(v) => onChange(ent, v)} disabled={disabled}>
      <SelectTrigger className="h-8 w-32 rounded-sm text-xs" data-testid={`entity-kind-${ent.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function Entities() {
  const api = useApi();
  const { period, me } = useApp();
  const ro = !me?.access?.can_edit;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api.get("/entities", { params: period ? { period } : {} }).then((r) => setRows(r.data.entities));
  }, [api, period]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => rows.filter((r) => (filter === "all" || r.kind === filter) &&
    `${r.name} ${r.code || ""} ${(r.aliases || []).join(" ")}`.toLowerCase().includes(q.toLowerCase())), [rows, q, filter]);

  const changeKind = async (ent, kind) => {
    try {
      await api.patch(`/entities/${ent.id}`, { kind });
      setRows((rs) => rs.map((r) => (r.id === ent.id ? { ...r, kind } : r)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">Entities</h1>
          <p className="text-slate-500 mt-3">Created automatically from uploads. Set the type so individuals get net worth statements.</p>
        </div>
        <Button variant="outline" disabled={ro} onClick={() => setEdit({ name: "", kind: "entity" })} data-testid="entity-add-button"><Plus className="h-4 w-4 mr-1" />Add entity</Button>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-10">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code" className="pl-9 w-72 rounded-sm bg-white" data-testid="entity-search-input" />
        </div>
        <div className="flex border border-slate-200 bg-white">
          {FILTERS.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} data-testid={`entity-filter-${f.value}`}
              className={`px-4 py-2 text-sm transition-colors duration-200 ${filter === f.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{f.label}</button>
          ))}
        </div>
        <span className="small-label ml-auto">{shown.length} shown</span>
      </div>
      <div className="panel mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]" data-testid="entities-table">
          <thead className="bg-slate-100"><tr className="text-left small-label"><th className="px-5 py-3">Name</th><th>Code</th><th>EID</th><th>Type</th><th className="text-right">Owned by</th><th className="text-right pr-5">Net assets</th><th /></tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors duration-200" data-testid={`entity-row-${r.id}`}>
                <td className="px-5 py-2.5">
                  <Link to={`/reports/${r.kind === "entity" ? "entities" : "networth"}/${r.id}`} className="text-slate-900 hover:text-amber-700 transition-colors duration-200">{r.name}</Link>
                  {!r.has_data && <span className="ml-2 text-[10px] uppercase font-mono text-slate-400">no balances</span>}
                </td>
                <td className="font-mono text-xs">{r.code || "—"}{r.aliases?.length ? <span className="text-slate-400"> · {r.aliases.join(", ")}</span> : null}</td>
                <td className="font-mono text-xs">{r.eid ?? "—"}</td>
                <td><KindSelect ent={r} onChange={changeKind} disabled={ro} /></td>
                <td className="text-right">
                  {r.owner_count ? <span className={Math.abs(r.owners_pct - 100) > 0.05 ? "warn-badge" : "num text-xs"}>{fmtPct(r.owners_pct)} · {r.owner_count}</span> : <span className="text-xs text-slate-400">top level</span>}
                </td>
                <td className="num text-right pr-5">{fmtMoney(r.net_assets)}</td>
                <td className="pr-3"><Button variant="ghost" size="sm" disabled={ro} onClick={() => setEdit(r)} data-testid={`entity-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EditDialog ent={edit} all={rows} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
